import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface AddSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (categoryName: string) => void
  sectionName: string
  existingCategories: string[]
}

export function AddSectionDialog({
  open,
  onOpenChange,
  onConfirm,
  sectionName,
  existingCategories,
}: AddSectionDialogProps) {
  const [categoryName, setCategoryName] = useState('')
  const [error, setError] = useState<string>('')

  const categoryExists = existingCategories.some(
    cat => cat.toLowerCase() === categoryName.trim().toLowerCase()
  )

  const handleConfirm = () => {
    const trimmedName = categoryName.trim()

    if (!trimmedName) {
      setError('Voer een sectienaam in')
      return
    }

    if (categoryExists) {
      setError('Deze sectie bestaat al')
      return
    }

    onConfirm(trimmedName)
    setCategoryName('')
    setError('')
    onOpenChange(false)
  }

  const handleCancel = () => {
    setCategoryName('')
    setError('')
    onOpenChange(false)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCategoryName('')
      setError('')
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nieuwe sectie toevoegen</DialogTitle>
          <DialogDescription>
            Voeg een nieuwe sectie toe aan {sectionName}. Geef de sectie een duidelijke naam.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="categoryName">Sectienaam</Label>
            <Input
              id="categoryName"
              placeholder="Bijv. Extra informatie, Overleg, etc."
              value={categoryName}
              onChange={e => {
                setCategoryName(e.target.value)
                setError('')
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !categoryExists && categoryName.trim()) {
                  handleConfirm()
                }
              }}
              autoFocus
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Annuleren
          </Button>
          <Button onClick={handleConfirm} disabled={!categoryName.trim() || categoryExists}>
            Toevoegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
